// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFieldValue from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/SetFieldValue";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ContactFormContext } from "@insite/content-library/Widgets/ContactUs/ContactForm";
import TextArea, { TextAreaProps } from "@tps/MobiusOverrides/TextArea/TextArea";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    label = "label",
    hintText = "hintText",
    isRequired = "isRequired",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.label]: string;
        [fields.hintText]: string;
        [fields.isRequired]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    message: state.components.contactUsForm.fieldValues["message"],
});

const mapDispatchToProps = {
    setFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface MessageStyles {
    messageTextArea?: TextAreaProps;
}

export const messageStyles: MessageStyles = {};

const styles = messageStyles;

const Message: React.FC<Props> = ({ fields, message, setFieldValue }) => {
    const { label, hintText, isRequired } = fields;
    const contactFormContext = React.useContext(ContactFormContext);
    const [messageErrorMessage, setMessageErrorMessage] = React.useState("");

    React.useEffect(() => {
        contactFormContext.validators.message = validateMessage;
        if (typeof message !== "undefined") {
            validateMessage();
        }
    }, [message]);

    React.useEffect(() => {
        contactFormContext.validators.message = validateMessage;
    }, [isRequired]);

    // XNG-Changes check if empty value
    const validateMessage = () => {
        const errorMessage =
            isRequired && (!message || message?.trim().length <= 0)
                ? (siteMessage("Field_Required", label) as string)
                : "";
        setMessageErrorMessage(errorMessage);
        return !errorMessage;
    };

    const messageChangeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setFieldValue({ key: "message", value: event.target.value });
    };

    return (
        <TextArea
            {...styles.messageTextArea}
            label={label}
            placeholder={hintText}
            value={message || ""}
            required={isRequired}
            error={messageErrorMessage}
            onChange={messageChangeHandler}
        />
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(Message),
    definition: {
        group: "Contact Us",
        icon: "input-pipe",
        fieldDefinitions: [
            {
                name: fields.label,
                displayName: "Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Message",
                isRequired: true,
            },
            {
                name: fields.hintText,
                displayName: "Hint Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "How can I help you?",
                isRequired: true,
            },
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
